import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { NgxDropzoneModule } from 'ngx-dropzone';

// models
import { CompanyInfo, User } from 'src/app/shared/models/company';
import { SharedService } from 'src/app/shared/services/shared.service';
import {
  Answers,
  FFLMonthCharacters,
  ModalType,
  Questions,
} from 'src/app/shared/models/constants';
import { Subscription } from 'rxjs';
import { calculateFFLExpirationDate, timeAgo } from 'src/app/shared/functions';
import { AtfFfl } from 'src/app/shared/models/atf-ffl';
import { FFL } from 'src/app/shared/models/general';

@Component({
  selector: 'app-account-management',
  templateUrl: './account-management.component.html',
  styleUrls: ['./account-management.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgxMaskDirective, NgxDropzoneModule]
})
export class AccountManagementComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  accountForm: FormGroup;
  paymentForm: FormGroup;
  usersData: User[] = [];
  logo: any[] = [];
  payment_types: string[] = ['Visa', 'Mastercard', 'Bank Account'];
  companyLogoUrl: string | null = null;
  logoFileName: string | null = null;
  logoFileSize: number | null = null;
  logoUploadedAt: string | null = null;
  uploadingLogo: boolean = false;
  removingLogo: boolean = false;
  uploadingFfl: boolean = false;
  uploadingSot: boolean = false;
  removingFfl: boolean = false;
  removingSot: boolean = false;
  fflValidated: boolean = false;
  fflInvalid: boolean = false;
  readonly timeAgo = timeAgo;

  constructor(
    private formBuilder: FormBuilder,
    public sharedService: SharedService
  ) {
    // initialize form
    this.accountForm = this.formBuilder.group({
      company_name: new FormControl({ value: null, disabled: false }),
      legal_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      ein: new FormControl({ value: null, disabled: false }),
      address: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      address2: new FormControl({ value: null, disabled: false }),
      city: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      state: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      zip_code: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      phone_number: new FormControl({ value: null, disabled: false }),
      company_email: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      customer_facing_email: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      website: new FormControl({ value: null, disabled: false }),
      ffl: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      ffl_expiration_date: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
    });
    // yes_no_submit
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          // response for correct FFL question
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.correct_ffl
          ) {
            const info = response['optional_details'] as FFL;
            this.patchFfl(info);
          }
        })
    );
    // user_to_add_or_edit
    this.subscriptions.add(
      this.sharedService.user_to_add_or_edit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (response['modal_type'] === ModalType['add']) {
            this.sharedService.addUser(response['form_values']);
          } else if (response['modal_type'] === ModalType['edit']) {
            this.sharedService.editUser(response['form_values']);
          }
        })
    );
    // company_info_retrieved
    this.subscriptions.add(
      this.sharedService.company_info_retrieved$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (response.company_name) {
            this.patchFormValues();
          }
        })
    );
  }
  ngOnInit(): void {
    this.patchFormValues();
  }
  async patchFormValues() {
    const company: CompanyInfo = this.sharedService.company_info;
    if (company.company_name || company.company_email) {
      if (company.ffl) {
        this.fflValidated = true;
      }
      this.accountForm.patchValue({
        company_name: company.company_name,
        legal_name: company.legal_name,
        ein: company.ein,
        address: company.address,
        address2: company.address2,
        city: company.city,
        state: company.state,
        zip_code: company.zip_code,
        phone_number: company.phone_number,
        company_email: company.company_email,
        customer_facing_email: company.customer_facing_email,
        website: company.website,
        ffl: company.ffl,
        ffl_expiration_date: calculateFFLExpirationDate(company.ffl),
      });

      // Fetch documents if they aren't loaded yet
      if (company.company_id) {
        if (!company.ffl_document) {
          const result = await this.sharedService.getFFLDocument(company.company_id);
          company.ffl_document = result.document;
          company.ffl_document_meta = {
            file_name: result.file_name,
            file_size_kb: result.file_size_kb,
            uploaded_at: result.updatedAt,
          };
        }
        if (!company.sot_document) {
          const result = await this.sharedService.getSOTDocument(company.company_id);
          company.sot_document = result.document;
          company.sot_document_meta = {
            file_name: result.file_name,
            file_size_kb: result.file_size_kb,
            uploaded_at: result.updatedAt,
          };
        }
      }
    }
  }
  saveAccountInfo() {
    this.sharedService.company_info.company_name =
      this.accountForm.value.company_name;
    this.sharedService.company_info.legal_name =
      this.accountForm.value.legal_name;
    // this.sharedService.company_info.company_logo = this.accountForm.value.company_logo;
    this.sharedService.company_info.ein = this.accountForm.value.ein;
    this.sharedService.company_info.address = this.accountForm.value.address;
    this.sharedService.company_info.address2 = this.accountForm.value.address2;
    this.sharedService.company_info.city = this.accountForm.value.city;
    this.sharedService.company_info.zip_code = this.accountForm.value.zip_code;
    this.sharedService.company_info.state = this.accountForm.value.state;
    this.sharedService.company_info.phone_number =
      this.accountForm.value.phone_number;
    this.sharedService.company_info.company_email =
      this.accountForm.value.company_email;
    this.sharedService.company_info.customer_facing_email =
      this.accountForm.value.customer_facing_email;
    this.sharedService.company_info.website = this.accountForm.value.website;
    this.sharedService.company_info.ffl = this.accountForm.value.ffl;
    this.sharedService.company_info.ffl_expiration_date =
      this.accountForm.value.ffl_expiration_date;
    this.sharedService.updateCompanyInformation(this.sharedService.company_info);
  }
  verifyFFL() {
    this.fflValidated = false;
    this.fflInvalid = false;
    let new_ffl = this.accountForm.value.ffl;
    if (new_ffl?.length === 20) {
      if (FFLMonthCharacters.includes(new_ffl.substring(13, 14))) {
        this.fetchFflDetails(new_ffl.replace(/-/g, '').toUpperCase());
      } else {
        this.fflInvalid = true;
        this.sharedService.raiseErrorToast(
          'FFL Month is not valid. Please enter a valid FFL.'
        );
        this.accountForm.patchValue({
          ffl_expiration_date: null,
        });
      }
    }
  }
  fetchFflDetails(ffl: string) {
    this.sharedService
      .verifyFflAtfList(ffl)
      .subscribe({
        next: (response: { message: string; ffl: AtfFfl }) => {
          let fetched_ffl: FFL = new FFL();
          fetched_ffl.ffl = ffl;
          fetched_ffl.legal_name = response.ffl?.license_name.toString();
          fetched_ffl.business_name = response.ffl.business_name.toString();
          fetched_ffl.premise_street = response.ffl.premise_street.toString();
          fetched_ffl.premise_city = response.ffl.premise_city.toString();
          fetched_ffl.premise_state = response.ffl.premise_state.toString();
          fetched_ffl.premise_zip_code = response.ffl.premise_zip_code.toString();
          fetched_ffl.phone_number = response.ffl.voice_telephone.toString();
          fetched_ffl.email = null;
          fetched_ffl.notes = null;
          fetched_ffl.issue_date = response.ffl.loa_issue_date
            ? response.ffl.loa_issue_date.toString()
            : null;
          fetched_ffl.expiration_date = response.ffl.loa_expiration_date
            ? response.ffl.loa_expiration_date.toString()
            : null;
          this.sharedService.openYesNoModal(
            Questions.correct_ffl,
            fetched_ffl,
            true
          );
        },
        error: (err) => {
          this.fflInvalid = true;
          console.error('Error fetching FFL details:', err);
          if (err.status === 404) {
            this.sharedService.raiseWarningToast(
              'Could not find a matching FFL number. Please check the number and try again.'
            );
          } else {
            this.sharedService.raiseErrorToast(
              'An unexpected error occurred while communicating with the server. Please try again later.'
            );
          }
        }
      });
  }
  patchFfl(response_details: FFL) {
    this.fflValidated = true;
    if (!this.accountForm.get('ffl').value) {
      this.accountForm.patchValue({
        ffl: response_details.ffl,
      });
    }
    this.accountForm.patchValue({
      company_name: response_details.business_name,
      legal_name: response_details.legal_name,
      address: response_details.premise_street,
      city: response_details.premise_city,
      state: response_details.premise_state,
      zip_code: response_details.premise_zip_code,
      phone_number: response_details.phone_number,
      company_email: response_details.email,
      ffl_expiration_date: response_details.expiration_date
        ? response_details.expiration_date
        : calculateFFLExpirationDate(response_details.ffl),
    });
    const businessNameControl = this.accountForm.get('company_name');
    businessNameControl.markAsDirty();
    businessNameControl.markAsTouched();
    businessNameControl.updateValueAndValidity();
  }
  async addLogo(event: any) {
    const file = event.addedFiles[0];
    if (!file) return;
    this.uploadingLogo = true;
    try {
      this.logoFileName = file.name;
      this.logoFileSize = Math.round(file.size / 1024);
      this.logoUploadedAt = new Date().toISOString();
      const url = await this.sharedService.uploadCompanyLogo(file);
      this.logo = [...event.addedFiles];
      this.companyLogoUrl = url;
      this.sharedService.company_info.company_logo = url;
    } catch (error) {
      console.error('Error uploading logo:', error);
      this.sharedService.raiseErrorToast('Logo upload failed.');
    } finally {
      this.uploadingLogo = false;
    }
  }
  async removeLogo() {
    this.removingLogo = true;
    try {
      await this.sharedService.deleteCompanyLogo();
      this.logo = [];
      this.sharedService.company_info.company_logo = null;
      this.companyLogoUrl = null;
      this.logoFileName = null;
      this.logoFileSize = null;
      this.logoUploadedAt = null;
    } catch (error) {
      console.error('Error removing logo:', error);
      this.sharedService.raiseErrorToast('Could not remove logo.');
    } finally {
      this.removingLogo = false;
    }
  }
  removeExistingLogo() {
    return this.removeLogo();
  }
  replaceLogo() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        this.addLogo({ addedFiles: [file] });
      }
    };
    input.click();
  }
  addFflDocument(event: any) {
    return this.uploadDocument('ffl', event);
  }
  replaceDocument(documentType: 'ffl' | 'sot') {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        this.uploadDocument(documentType, { addedFiles: [file] });
      }
    };
    input.click();
  }
  removeFflDocument() {
    return this.deleteDocument('ffl');
  }
  addSotDocument(event: any) {
    return this.uploadDocument('sot', event);
  }
  removeSotDocument() {
    return this.deleteDocument('sot');
  }
  private async uploadDocument(documentType: 'ffl' | 'sot', event: any) {
    const file = event.addedFiles[0];
    if (!file) return;
    const uploadingKey = `uploading${documentType === 'ffl' ? 'Ffl' : 'Sot'}`;
    this[uploadingKey] = true;
    try {
      const result = await this.sharedService.uploadCompanyDocument(documentType, file);
      this.sharedService.company_info[`${documentType}_document`] = result.url;
      this.sharedService.company_info[`${documentType}_document_meta`] = {
        file_name: result.file_name,
        file_size_kb: result.file_size_kb,
        uploaded_at: result.updatedAt,
      };
    } catch (error) {
      console.error(`Error uploading ${documentType.toUpperCase()} PDF:`, error);
      this.sharedService.raiseErrorToast(`${documentType.toUpperCase()} upload failed.`);
    } finally {
      this[uploadingKey] = false;
    }
  }
  private async deleteDocument(documentType: 'ffl' | 'sot') {
    const removingKey = `removing${documentType === 'ffl' ? 'Ffl' : 'Sot'}`;
    this[removingKey] = true;
    try {
      await this.sharedService.deleteCompanyDocument(documentType);
      this.sharedService.company_info[`${documentType}_document`] = null;
      this.sharedService.company_info[`${documentType}_document_meta`] = null;
    } catch (error) {
      console.error(`Error removing ${documentType.toUpperCase()} PDF:`, error);
      this.sharedService.raiseErrorToast(`Could not remove ${documentType.toUpperCase()}.`);
    } finally {
      this[removingKey] = false;
    }
  }
  formatFileSize(sizeKb: number | null | undefined): string {
    if (!sizeKb) return '';
    return sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${Math.round(sizeKb)} KB`;
  }
  viewDocument(documentUrl: string) {
    if (!documentUrl) return;
    // Allow legacy data URIs, object URLs, and Azure Blob SAS URLs only —
    // anything else (javascript:, arbitrary hosts) must not reach an iframe src.
    const isSafeUrl =
      documentUrl.startsWith('data:application/pdf') ||
      documentUrl.startsWith('blob:') ||
      /^https:\/\/[a-z0-9-]+\.blob\.core\.windows\.net\//i.test(documentUrl);
    if (!isSafeUrl) return;
    const win = window.open();
    if (win) {
      const iframe = win.document.createElement('iframe');
      iframe.src = documentUrl;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', '');
      iframe.style.cssText =
        'border:0;top:0;left:0;bottom:0;right:0;width:100%;height:100%;position:fixed;';
      win.document.body.appendChild(iframe);
    }
  }
}

